import { useLocation } from "wouter";
import * as ReactHelmetAsyncModule from "react-helmet-async";
import { BASE_URL, getRouteSeoData, type LanguageCode } from "@/site-config";

const helmetAsyncCompat = ReactHelmetAsyncModule as Record<string, any>;
const Helmet =
  helmetAsyncCompat.Helmet ||
  helmetAsyncCompat["default"]?.Helmet;

interface SEOHeadProps {
  pathname?: string;
  language?: LanguageCode;
  title?: string;
  description?: string;
  ogImage?: string;
  noIndex?: boolean;
  ogType?: string;
}

export function SEOHead({
  pathname,
  language,
  title,
  description,
  ogImage,
  noIndex,
  ogType = "website",
}: SEOHeadProps) {
  const [location] = useLocation();
  const routeSeo = getRouteSeoData(pathname || location);
  const resolvedLanguage = language || routeSeo.language;
  const resolvedTitle = title || routeSeo.title;
  const resolvedDescription = description || routeSeo.description;
  const resolvedOgTitle = routeSeo.ogTitle || resolvedTitle;
  const resolvedOgDescription = routeSeo.ogDescription || resolvedDescription;
  const resolvedOgImage = `${BASE_URL}${ogImage || routeSeo.ogImage}`;
  const shouldNoIndex = noIndex ?? routeSeo.noIndex;

  return (
    <Helmet prioritizeSeoTags>
      <html lang={resolvedLanguage} />
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={routeSeo.canonicalUrl} />

      {routeSeo.alternates.map(({ hrefLang, href }) => (
        <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />
      ))}

      <meta property="og:title" content={resolvedOgTitle} />
      <meta property="og:description" content={resolvedOgDescription} />
      <meta property="og:url" content={routeSeo.canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={resolvedOgImage} />
      <meta property="og:site_name" content="Rugira" />
      <meta
        property="og:locale"
        content={
          resolvedLanguage === "de"
            ? "de_CH"
            : resolvedLanguage === "fr"
              ? "fr_CH"
              : resolvedLanguage === "it"
                ? "it_CH"
                : "en_US"
        }
      />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedOgTitle} />
      <meta name="twitter:description" content={resolvedOgDescription} />
      <meta name="twitter:image" content={resolvedOgImage} />

      <meta name="robots" content={shouldNoIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="googlebot" content={shouldNoIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="language" content={resolvedLanguage} />
      <meta name="geo.region" content="CH" />
      <meta name="geo.placename" content="Zug" />
    </Helmet>
  );
}
